import { Eye, Heart, ShoppingCart, SlidersHorizontal } from "lucide-react";
import { motion } from "motion/react";
import type { Product } from "../../types";
import { cn, formatPrice } from "../../utils/format";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { QuantitySelector } from "../ui/QuantitySelector";
import { Stars } from "../ui/Stars";

interface ProductCardProps {
  product: Product;
  /** Unidades no carrinho (somando todas as personalizações). */
  quantity: number;
  favorite: boolean;
  /** Lanche com ingredientes/adicionais: o botão abre a personalização. */
  customizable?: boolean;
  onAdd: () => void;
  onDecrement: () => void;
  onToggleFavorite: () => void;
  onDetails: () => void;
  /** Posição dentro do "lote" carregado — escalona a entrada. */
  index?: number;
}

export const ProductCard = ({
  product,
  quantity,
  favorite,
  customizable = false,
  onAdd,
  onDecrement,
  onToggleFavorite,
  onDetails,
  index = 0,
}: ProductCardProps) => (
  <motion.li
    layout
    initial={{ opacity: 0, y: 24, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, scale: 0.94, transition: { duration: 0.18 } }}
    transition={{ duration: 0.4, delay: Math.min(index, 8) * 0.04, ease: [0.22, 1, 0.36, 1] }}
    className="h-full"
  >
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className={cn(
        "group flex h-full flex-col rounded-card border bg-card p-4 transition-colors duration-300",
        quantity > 0 ? "border-primary/60" : "border-line hover:border-primary/40",
      )}
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-night">
        <img
          src={product.image_url || "/favicon.png"}
          alt={product.name}
          loading="lazy"
          className="size-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
        />
        {/* Clique na foto (atalho do mouse; no teclado o botão de olho faz o mesmo). */}
        <button
          type="button"
          tabIndex={-1}
          onClick={onDetails}
          aria-label={customizable ? `Personalizar ${product.name}` : `Ver detalhes de ${product.name}`}
          className="absolute inset-0 cursor-pointer"
        />
        {quantity > 0 && (
          <Badge variant="solid" className="absolute left-2.5 top-2.5">
            {quantity} no carrinho
          </Badge>
        )}
        <motion.button
          type="button"
          whileTap={{ scale: 0.8 }}
          onClick={onToggleFavorite}
          aria-pressed={favorite}
          aria-label={favorite ? `Remover ${product.name} dos favoritos` : `Favoritar ${product.name}`}
          className="absolute right-2.5 top-2.5 grid size-9 place-items-center rounded-full bg-night/70 backdrop-blur transition-colors hover:bg-night"
        >
          <Heart
            className={cn("size-[18px] transition-colors", favorite ? "fill-primary text-primary" : "text-white")}
            aria-hidden="true"
          />
        </motion.button>
      </div>

      <div className="flex flex-1 flex-col pt-4 text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">{product.category}</p>
        {product.rating !== undefined && <Stars rating={product.rating} className="mt-2 justify-center" />}
        <h3 className="mt-1.5 font-display text-[1.75rem] leading-none tracking-wide">{product.name}</h3>
        <p className="mt-2 line-clamp-2 min-h-10 text-sm text-muted">{product.description}</p>
        <p className="mt-3 text-xl font-bold text-primary">{formatPrice(product.price)}</p>

        <div className="mt-auto flex items-center justify-center gap-2 pt-4">
          <button
            type="button"
            onClick={onDetails}
            aria-label={customizable ? `Ver ingredientes de ${product.name}` : `Ver detalhes de ${product.name}`}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-line text-muted transition-colors hover:border-primary hover:bg-primary hover:text-night"
          >
            <Eye className="size-4" aria-hidden="true" />
          </button>
          {customizable ? (
            // Cada personalização é uma linha do carrinho: aqui sempre abre a tela de personalização.
            <Button
              size="sm"
              variant={quantity > 0 ? "primary" : "outline"}
              onClick={onAdd}
              icon={<SlidersHorizontal className="size-4" aria-hidden="true" />}
              iconPosition="left"
              className={cn("px-3.5", quantity === 0 && "hover:!border-primary hover:!bg-primary hover:!text-night")}
            >
              {quantity > 0 ? "Personalizar outro" : "Personalizar"}
            </Button>
          ) : quantity > 0 ? (
            <QuantitySelector quantity={quantity} label={product.name} onIncrement={onAdd} onDecrement={onDecrement} size="sm" />
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={onAdd}
              icon={<ShoppingCart className="size-4" aria-hidden="true" />}
              iconPosition="left"
              className="px-3.5 hover:!border-primary hover:!bg-primary hover:!text-night"
            >
              Adicionar ao carrinho
            </Button>
          )}
        </div>
      </div>
    </motion.article>
  </motion.li>
);

export const ProductCardSkeleton = () => (
  <div className="flex h-full animate-pulse flex-col rounded-card border border-line bg-card p-4" aria-hidden="true">
    <div className="aspect-[4/3] rounded-xl bg-line/60" />
    <div className="mx-auto mt-5 h-3 w-20 rounded bg-line/60" />
    <div className="mx-auto mt-3 h-6 w-36 rounded bg-line/60" />
    <div className="mx-auto mt-3 h-3 w-full rounded bg-line/40" />
    <div className="mx-auto mt-2 h-3 w-4/5 rounded bg-line/40" />
    <div className="mx-auto mt-4 h-6 w-24 rounded bg-line/60" />
    <div className="mx-auto mt-5 h-9 w-48 rounded-full bg-line/50" />
  </div>
);
